import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { user, loginWithPassword, loginWithPin } = useAuth();

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const [loginMethod, setLoginMethod] = useState<'password' | 'pin'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await loginWithPassword(email, password);
      navigate('/dashboard');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePinLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await loginWithPin(email, pin);
      navigate('/dashboard');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'PIN login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#0a0a0a]">
      {/* Subtle radial glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-[0.03]" style={{ background: 'radial-gradient(circle, #E8651A 0%, transparent 70%)' }} />
      </div>

      <div className="relative w-full max-w-sm mx-4">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E8651A, #F5922B)' }}>
              <span className="text-[#0a0a0a] font-bold text-base">CG</span>
            </div>
          </div>
          <h1 className="text-xl font-bold text-[#f5f5f5] tracking-tight">Custom Group</h1>
          <p className="text-xs text-[#555] mt-1">CMS Access</p>
        </div>

        {/* Card */}
        <div className="bg-[#0a0a0a] border border-[#222] p-8">
          {/* Accent bar */}
          <div className="w-8 h-0.5 mb-6" style={{ background: 'linear-gradient(90deg, #E8651A, #F5922B)' }} />

          {loginMethod === 'password' ? (
            <form onSubmit={handlePasswordLogin} className="space-y-5">
              <div>
                <label className="block text-xs text-[#888] uppercase tracking-wider mb-2">Email</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="w-full bg-[#111] border border-[#333] rounded px-4 py-3 text-sm text-[#f5f5f5] placeholder-[#444] focus:outline-none focus:border-[#E8651A] transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs text-[#888] uppercase tracking-wider mb-2">Password</label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  className="w-full bg-[#111] border border-[#333] rounded px-4 py-3 text-sm text-[#f5f5f5] placeholder-[#444] focus:outline-none focus:border-[#E8651A] transition-colors"
                />
              </div>

              {error && (
                <div className="bg-red-900/20 border border-red-800/50 text-red-400 px-4 py-3 rounded text-xs">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #E8651A 0%, #F5922B 100%)' }}
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : null}
                Sign In
              </button>

              <button
                type="button"
                onClick={() => { setLoginMethod('pin'); setError(''); }}
                className="w-full text-xs text-[#888] hover:text-[#E8651A] transition-colors"
              >
                Sign in with PIN instead
              </button>
            </form>
          ) : (
            <form onSubmit={handlePinLogin} className="space-y-5">
              <div>
                <label className="block text-xs text-[#888] uppercase tracking-wider mb-2">Email</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="w-full bg-[#111] border border-[#333] rounded px-4 py-3 text-sm text-[#f5f5f5] placeholder-[#444] focus:outline-none focus:border-[#E8651A] transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs text-[#888] uppercase tracking-wider mb-2">PIN Code</label>
                <input
                  type="text"
                  placeholder="123456"
                  value={pin}
                  onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  required
                  className="w-full bg-[#111] border border-[#333] rounded px-4 py-3 text-lg text-center text-[#f5f5f5] tracking-[0.3em] placeholder-[#444] focus:outline-none focus:border-[#E8651A] transition-colors"
                />
              </div>

              {error && (
                <div className="bg-red-900/20 border border-red-800/50 text-red-400 px-4 py-3 rounded text-xs">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #E8651A 0%, #F5922B 100%)' }}
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : null}
                Sign In with PIN
              </button>

              <button
                type="button"
                onClick={() => { setLoginMethod('password'); setError(''); }}
                className="w-full text-xs text-[#888] hover:text-[#E8651A] transition-colors"
              >
                Sign in with password instead
              </button>
            </form>
          )}
        </div>

        {/* Back link */}
        <div className="text-center mt-6">
          <Link to="/" className="text-xs text-[#555] hover:text-[#888] transition-colors">
            ← Back to site
          </Link>
        </div>
      </div>
    </div>
  );
};
